import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Divider, DropdownSelect, Text, ToggleButton, ToggleButtonGroup } from '@zillow/constellation-10';

import { PropertiesSelectors, UserSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';

import PropertiesActions from '../../../actions/PropertiesActions';

import PropertiesConstants from '../../../constants/PropertiesConstants';
import PropertyConstants from '../../../constants/PropertyConstants';
import PropertiesPageCSEvents from '../../../model/ClickstreamEvent/PropertiesPageCSEvents';

import { ControlledSearchBox } from '../../../components/ControlledSearchBox';

import { type ListingCountDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

const { PROPERTIES_SUBNAV_FILTER_OPTIONS, PROPERTIES_SUBNAV_FILTER_TYPE } = PropertiesConstants;
const { PROPERTY_TABLE_TRIGGER_OBJECT_MAP } = PropertyConstants;

const baseHideableTabs = [
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK,
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS,
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
];

const groupedHideableTabs = [
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET,
  PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET,
];

const subNavFilterOptions = {
  options: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_OPTIONS.reduce(
    (acc, subNav) => {
      acc[subNav.id] = { ...subNav };

      return acc;
    },
    {} as Record<string, PROPERTIES_SUBNAV_FILTER_OPTIONS_TYPE>,
  ),
};

type PROPERTIES_SUBNAV_FILTER_OPTIONS_TYPE = (typeof PropertiesConstants.PROPERTIES_SUBNAV_FILTER_OPTIONS)[0];

type Update = {
  includeActive: boolean;
  includeInactive: boolean;
  includeArchived: boolean;
  includeUnarchived: boolean;
  subNavFilterType: string;
  startKeyExclusive: string;
  includeAtRisk: boolean;
  includeFreeBonusListingsOnly: boolean;
};

type Props = {
  searchText: string;
  listingsLoading: boolean;
  onSearchFilterUpdate: (arg: string) => void;
  createQueryObjectAndRedirectTo: (searchText: string, subNavFilterType: string) => void;
  updateFiltersAndListings: ({ update }: { update: Update }) => void;
  numDisplayedListings: number;
  listingCount: ListingCountDto;
};

const GroupableSubHeadingSection = ({
  listingCount,
  searchText,
  listingsLoading,
  onSearchFilterUpdate,
  createQueryObjectAndRedirectTo,
  updateFiltersAndListings,
  numDisplayedListings: _numDisplayedListings,
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  // Local state for immediate UI feedback
  const [searchInput, setSearchInput] = useState(searchText);

  // Sync searchInput with searchText when it changes from external sources
  useEffect(() => {
    setSearchInput(searchText);
  }, [searchText]);

  const {
    active: numActiveListings,
    inactive: numInactiveListings,
    archived: numArchivedListings,
    total: numTotalListings,
  } = listingCount;

  const { subNavFilterType } = useSelector((state) => PropertiesSelectors.makePropertiesFilters(state));
  const user = useSelector((state) => UserSelectors.makeUser(state));

  const canViewSearch = numTotalListings ?? 0 >= 5;

  const subNavFiltersOnSelectHandler = ({
    includeActive,
    includeInactive,
    id: subNavFilterType,
    includeAtRisk,
    includeFreeBonusListingsOnly,
    includeArchived,
    includeUnarchived,
  }: {
    includeActive: boolean;
    includeInactive: boolean;
    id: string;
    includeAtRisk: boolean;
    includeFreeBonusListingsOnly: boolean;
    includeArchived: boolean;
    includeUnarchived: boolean;
  }) => {
    dispatch(PropertiesActions.updateFiltersTouched(true));

    createQueryObjectAndRedirectTo(searchText, subNavFilterType);

    updateFiltersAndListings({
      update: {
        includeActive,
        includeInactive,
        includeArchived,
        includeUnarchived,
        subNavFilterType,
        startKeyExclusive: PropertiesConstants.PROPERTIES_START_KEY_EXCLUSIVE_DEFAULT,
        includeAtRisk,
        includeFreeBonusListingsOnly,
      },
    });

    PropertiesPageCSEvents.PropertyListFilter({
      user,
      triggerObject: PROPERTY_TABLE_TRIGGER_OBJECT_MAP.FILTER_TRIGGERS[subNavFilterType],
    });
  };

  const countMap = {
    [PROPERTIES_SUBNAV_FILTER_TYPE.ALL]: numTotalListings,
    [PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET]: numActiveListings,
    [PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET]: numInactiveListings,
    [PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED]: numArchivedListings,
  };
  PROPERTIES_SUBNAV_FILTER_OPTIONS.forEach((subNav) => {
    subNavFilterOptions.options[subNav.id].count = countMap[subNav.id] || 0;
  });

  const placeholderText = 'Search by city, address and ZIP code';

  const onFilterChange = (value: string | Array<string>) => {
    const selectedFilterType = Array.isArray(value) ? value[0] : value;
    subNavFiltersOnSelectHandler(subNavFilterOptions.options[selectedFilterType]);
  };

  const renderToggleButton = (id: string, label: string, count: number, selected?: boolean) => {
    const isBaseHideableTab = baseHideableTabs.includes(id) && count === 0 && id !== subNavFilterType;
    const isGroupedHideableTab = groupedHideableTabs.includes(id) && count === 0 && numTotalListings === 0;

    if (isBaseHideableTab || isGroupedHideableTab) {
      return null;
    }

    return (
      <ToggleButton
        key={id}
        value={id}
        disabled={listingsLoading}
        selected={selected}
        css={{ xlDown: { width: '100%' } }}
        data-eventid={PropertiesPageCSEvents.PropertyListFilter.data_event_id}
        data-testid={`PropertiesPage-Filters-${id}`}
      >
        {label} {numTotalListings > 0 && <Text textStyle="body-sm-bold">{`(${count})`}</Text>}
      </ToggleButton>
    );
  };

  const getDropdownOptions = () => {
    const options = [];

    if (!(baseHideableTabs.includes(PROPERTIES_SUBNAV_FILTER_TYPE.ALL) && numTotalListings === 0)) {
      options.push({ label: `All (${numTotalListings})`, value: PROPERTIES_SUBNAV_FILTER_TYPE.ALL });
    }
    if (!(baseHideableTabs.includes(PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET) && numActiveListings === 0)) {
      options.push({ label: `For rent (${numActiveListings})`, value: PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET });
    }
    if (!(baseHideableTabs.includes(PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET) && numInactiveListings === 0)) {
      options.push({ label: `Off market (${numInactiveListings})`, value: PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET });
    }
    if (numArchivedListings > 0 || subNavFilterType === PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED) {
      options.push({ label: `Archived (${numArchivedListings})`, value: PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED });
    }

    return options;
  };

  const renderSearchAndFilters = () => (
    <Box
      data-testid="GroupableSubHeadingSection"
      css={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: { base: 'column-reverse', xl: 'row' },
        marginTop: { base: '600', lg: '900' },
        gap: { base: '200', lg: '600' },
      }}
    >
      <Box css={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
        {/* Shown on lg screens and above */}
        <ToggleButtonGroup
          aria-label="Filter property list by status"
          size="sm"
          css={{ xlDown: { width: '100%' }, display: { base: 'none', lg: 'flex' } }}
          onValueChange={onFilterChange}
          value={subNavFilterType}
          data-testid="PropertiesPage-Filters-AllButton"
        >
          {renderToggleButton(PROPERTIES_SUBNAV_FILTER_TYPE.ALL, 'All', numTotalListings)}
          {renderToggleButton(PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET, 'For rent', numActiveListings)}
          {renderToggleButton(PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET, 'Off market', numInactiveListings)}
        </ToggleButtonGroup>

        {/* archived toggle button */}
        {(numArchivedListings > 0 || subNavFilterType === PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED) && (
          <Box css={{ display: { base: 'none', lg: 'flex' }, alignItems: 'center' }}>
            <Divider orientation="vertical" css={{ height: '900', marginInline: '400' }} />

            <Box
              aria-disabled={listingsLoading}
              onClick={() => !listingsLoading && onFilterChange(PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED)}
            >
              {renderToggleButton(
                PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
                'Archived',
                numArchivedListings,
                subNavFilterType === PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Shown on md screens and below */}
      <DropdownSelect
        key={`${subNavFilterType}-${numTotalListings}-${numActiveListings}-${numInactiveListings}`}
        value={subNavFilterType}
        options={getDropdownOptions()}
        showLabelForValue
        css={{ width: '100%', display: { base: 'flex', lg: 'none' } }}
        onChange={onFilterChange}
        disabled={listingsLoading}
        data-eventid={PropertiesPageCSEvents.PropertyListFilter.data_event_id}
        size="sm"
      />

      {canViewSearch && (
        <Box css={{ width: { base: '100%', xl: '50%' } }}>
          <ControlledSearchBox
            placeholder={placeholderText}
            value={searchInput}
            onChange={setSearchInput}
            onSearch={onSearchFilterUpdate}
            isSearching={listingsLoading && Boolean(searchText)}
            disabled={listingsLoading}
            iconSize="sm"
          />
        </Box>
      )}
    </Box>
  );

  return renderSearchAndFilters();
};

export default GroupableSubHeadingSection;
